import React, { useState, useLayoutEffect } from "react";
import styled from "styled-components";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4maps from "@amcharts/amcharts4/maps";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import am4geodata_data_countries2 from "../../../data/geo/countries2"
import am4geodata_worldIndiaLow from "../../../data/geo/worldIndiaLow"
import { MAP_COLORS } from "../../../constants/maps";
import usaLow from "../../../data/geo/usaLow";

const ChartDiv = styled.div`
  @media (max-width:  575px) {
    display:inline-block;
  }
`;

type MapViewProps = {
  data: any,
  isGlobalMap: boolean
}

const MapView = (props: MapViewProps) => {
  const _chartId = Math.random().toString(36).substring(2, 15);
  let chart_: any;
  let world_chart_: any;
  am4core.useTheme(am4themes_animated);
  am4core.addLicense("MP286846601");
  am4core.addLicense("CH287053715");

  const { data, isGlobalMap } = props;
  const [multiMatchesByLoc] = useState(data);
  const [isLoading] = useState(false);

  useLayoutEffect(() => {
    if (Object.keys(multiMatchesByLoc).length === 0 || isLoading) {
      return;
    }
    if (isGlobalMap) {
      _worldChart();
    } else {
      _usChart();
    }
  }, [multiMatchesByLoc, isGlobalMap]);



  const _usChart = () => {
    const _total = multiMatchesByLoc['base_filter_count'] || 1;
    chart_ = am4core.create(_chartId, am4maps.MapChart);
    chart_.geodata = usaLow; // Set map definition
    chart_.projection = new am4maps.projections.AlbersUsa(); // Set projection
    chart_.maxZoomLevel = 1;
    chart_.seriesContainer.draggable = false;
    chart_.seriesContainer.resizable = false;

    // Create map polygon series
    let polygon_series_ = chart_.series.push(new am4maps.MapPolygonSeries());
    polygon_series_.useGeodata = true;
    polygon_series_.calculateVisualCenter = true;

    // Configure series
    let polygon_template_ = polygon_series_.mapPolygons.template;
    polygon_template_.tooltipText = "{name}";
    // polygon_template_.fill = am4core.color("#4798ff");

    const loadData = () => {
      // @ts-ignore
      let data: any = multiMatchesByLoc['target_locations'] || [];
      let regional_series: any = {};
      regional_series.US = {
        markerData: [],
        series: createSeries("count")
      };
      am4core.array.each(data, (store: any) => {

        // Process state-level data
        if (regional_series[store.state_short] == undefined) {
          var statePolygon: any = polygon_series_.getPolygonById("US-" + store.state_short);
          if (statePolygon) {
            const _ratio = (store.profile_count / _total);
            const _color = MAP_COLORS[Math.ceil(_ratio * 10)];
            // Add state data
            regional_series[store.state_short] = {
              target: store.state_short,
              type: "state",
              name: statePolygon.dataItem.dataContext.name,
              count: store.profile_count,
              lat: statePolygon.visualLatitude,
              long: statePolygon.visualLongitude,
              state: store.state_short,
              markerData: [],
              color: _color
            };
            regional_series.US.markerData.push(regional_series[store.state_short]);
          }
          else {
            // State not found
            return;
          }
        }
        else {
          regional_series[store.state_short].count += store.profile_count;
        }
      });

      regional_series.US.series.data = regional_series.US.markerData;
    }

    const createSeries = (heatfield: any) => {
      let series = chart_.series.push(new am4maps.MapImageSeries());
      series.dataFields.value = heatfield;

      let template = series.mapImages.template;
      template.verticalCenter = "middle";
      template.horizontalCenter = "middle";
      template.propertyFields.latitude = "lat";
      template.propertyFields.longitude = "long";
      template.tooltipText = "{name}:\n[bold]{count} profiles[/]";

      let circle = template.createChild(am4core.Circle);
      circle.fillOpacity = 0.7;
      circle.propertyFields.fill = "color";

      series.heatRules.push({
        "target": circle,
        "property": "radius",
        "min": 4,
        "max": 12,
        "dataField": "value"
      })
      return series;
    }
    chart_.events.on("ready", loadData);
    chart_.validateData();
    return () => {
      chart_?.dispose();
    }
  };

  const _worldChart = () => {
    // Create map instance
    // @ts-ignore
    const _data = multiMatchesByLoc['target_locations'] || [];
    // @ts-ignore
    const _total = multiMatchesByLoc['base_filter_count'] || 1;
    world_chart_ = am4core.create(_chartId, am4maps.MapChart);
    world_chart_.projection = new am4maps.projections.Miller();
    world_chart_.chartContainer.wheelable = false;
    world_chart_.maxZoomLevel = 1;
    world_chart_.seriesContainer.draggable = false;
    world_chart_.seriesContainer.resizable = false;

    // Create map polygon series for world map
    var worldSeries = world_chart_.series.push(new am4maps.MapPolygonSeries());
    worldSeries.useGeodata = true;
    worldSeries.geodata = am4geodata_worldIndiaLow;
    worldSeries.exclude = ["AQ"];
    var worldPolygon = worldSeries.mapPolygons.template;
    worldPolygon.tooltipText = "{name}";
    worldPolygon.adapter.add("tooltipText", (text: string, target: any) => {
      // @ts-ignore
      let _globalData: any = _data;
      if (_globalData) {
        const _country = _globalData.find((countryCount: any) => target.dataItem.dataContext.name === countryCount.country);
        if (_country?.profile_count > 0) {
          return `{name}:\n[bold]${_country.profile_count} profile(s)[/]`;
        } else {
          return "{name}\n No results matching the selected filters";
        }

      }
    });
    worldPolygon.nonScalingStroke = true;
    worldPolygon.strokeOpacity = 0.5;
    worldPolygon.fill = am4core.color("#f2f2f2");
    worldPolygon.propertyFields.fill = "color";


    // Set up data for countries
    var data = [];
    let count_by_country: any = {};
    let ccode_by_name: any = {};
    if (_data) {
      _data.forEach((s: any) => {
        count_by_country[s.country_short] = s.profile_count;
        ccode_by_name[s.country_short] = s.country;
      })
    }

    worldSeries.events.on("inited", function () {
      worldSeries.mapPolygons.each(function (polygon: any) {
        var id = polygon.dataItem.dataContext.id.split("-").pop();
        if (count_by_country[id]) {
          // label.latitude = polygon.visualLatitude;
          // label.longitude = polygon.visualLongitude;
          // label.children.getIndex(0).text = '[bold]' + ccode_by_name[id] + '\n' + count_by_country[id];
        }
      });
    });

    for (var id in am4geodata_data_countries2) {
      if (am4geodata_data_countries2.hasOwnProperty(id)) {
        // @ts-ignore
        var country = am4geodata_data_countries2[id];
        let count = count_by_country[id];
        if (count) {
          const _ratio = (count / _total);
          const _color = MAP_COLORS[Math.ceil(_ratio * 10)];
          data.push({
            target: id,
            id: id,
            name: ccode_by_name[id],
            type: 'country',
            value: count_by_country[id],
            // @ts-ignore
            color: am4core.color(_color),
            map: country.maps[0]
          });
        }
      }
    }
    // worldSeries.data = data;
    worldSeries.data = data;
    // Zoom control
    world_chart_.zoomControl = new am4maps.ZoomControl();
    world_chart_.zoomControl.plusButton.hide();
    world_chart_.zoomControl.minusButton.hide();
    var imageSeries = world_chart_.series.push(new am4maps.MapImageSeries());
    imageSeries.data = data;
    imageSeries.dataFields.value = "value";
    world_chart_.chartContainer.wheelable = false;
  }

  return (
    <>
      <div className="map-view-container">
        <div className="map-container">
          <ChartDiv id={_chartId} className="chartdiv" style={{ height: "168px", width: "229px" }}></ChartDiv>
        </div>
      </div>
    </>
  );
};

export default MapView;
